<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    header="Review Changes"
    class="w-full max-w-lg"
    data-test-id="row-save-dialog"
    :closable="false"
  >
    <div class="flex flex-col gap-3 py-2">
      <p class="text-sm text-surface-600 dark:text-surface-300">
        You are about to save the following changes for
        <strong>{{ countryName }}</strong>:
      </p>

      <ul class="list-none p-0 m-0 flex flex-col gap-2">
        <li
          v-for="change in changes"
          :key="change.field"
          class="flex items-start gap-2 text-sm"
        >
          <span class="font-medium text-surface-700 dark:text-surface-200 min-w-24">
            {{ change.field }}:
          </span>
          <span class="text-surface-600 dark:text-surface-300">
            <span class="line-through text-surface-400 dark:text-surface-500">{{ change.from }}</span>
            &nbsp;→&nbsp;
            <span class="font-semibold text-primary">{{ change.to }}</span>
          </span>
        </li>
      </ul>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2 mt-2">
        <Button
          label="Cancel"
          severity="secondary"
          :disabled="submitting"
          @click="onCancel"
          data-test-id="row-save-cancel"
        />
        <Button
          label="Save"
          icon="pi pi-save"
          :loading="submitting"
          :disabled="submitting"
          @click="onConfirm"
          data-test-id="row-save-confirm"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { Button, Dialog } from 'primevue'

export interface ChangeEntry {
  field: string
  from: string
  to: string
}

interface Props {
  visible: boolean
  countryName: string
  changes: ChangeEntry[]
  submitting?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  submitting: false,
})

const emit = defineEmits<{
  'update:visible': [value: boolean]
  confirm: []
  cancel: []
}>()

const isVisible = ref(props.visible)

watch(
  () => props.visible,
  v => {
    isVisible.value = v
  },
)

watch(isVisible, v => {
  emit('update:visible', v)
})

function onCancel() {
  emit('cancel')
  isVisible.value = false
}

function onConfirm() {
  emit('confirm')
}
</script>
